import { AppleNotesStepsMap } from "@/components/onboarding/components/welcome-wizard/steps/import/apple-notes-steps";
import { ObsidianStepsMap } from "@/components/onboarding/components/welcome-wizard/steps/import/obsidian-steps";
import { OtherAppsStepsMap } from "@/components/onboarding/components/welcome-wizard/steps/import/other-apps-steps";
import { PostImportStepsMap } from "@/components/onboarding/components/welcome-wizard/steps/post-import";
import type {
  StepMap,
  StepGroup,
  AppId,
} from "@/components/onboarding/components/welcome-wizard/welcome-wizard.types";
import { AppSelectionStep } from "@/components/onboarding/components/welcome-wizard/steps/import/app-selection-step";
import { SkipConfirmationStep } from "@/components/onboarding/components/welcome-wizard/steps/import/skip-confirmation-step";
import { IcpRfiStepsMap } from "@/components/onboarding/components/welcome-wizard/steps/onboarding/icp-rfi-step";
import { ReferrerStepsMap } from "@/components/onboarding/components/welcome-wizard/steps/onboarding/referrer-step";
import { NotesApps } from "@/components/onboarding/components/welcome-wizard/steps/import/import-steps.config";
import { NativeAppDownloadStepMap } from "@/components/onboarding/components/welcome-wizard/steps/native-app-download-steps";
import { SystemPermissionsMeetingsStepsMap } from "@/components/onboarding/components/welcome-wizard/steps/system-permissions-meetings";
import { SystemPermissionsVoiceStepsMap } from "@/components/onboarding/components/welcome-wizard/steps/system-permission-voice";

// Each group corresponds to an onboarding stage that can be _dismissed_ through the API
export const StepGroups: Record<string, StepGroup> = {
  NATIVE_APP_UPSELL: {
    id: "native-app-upsell",
    title: "Download Mem",
    steps: ["native-app-download"],
    showTabs: false,
  },
  // ICP RFI = "ideal customer profile request for information"
  ICP_RFI: {
    id: "onboarding",
    title: "Welcome to Mem",
    steps: ["user-profile", "referrer"],
    showTabs: false,
  },
  IMPORT: {
    id: "import",
    title: "Import Your Notes",
    steps: [
      "app-selection",
      "skip-confirmation",
      "apple-notes-1",
      "apple-notes-2",
      "apple-notes-3",
      "obsidian-1",
      "obsidian-2",
      "other-apps-1",
    ],
    showTabs: true,
    tabConfig: { tabs: NotesApps },
  },
  POST_IMPORT: {
    id: "post-import",
    title: "Post Import",
    steps: ["download-apps", "intro-video"],
    showTabs: false,
  },
  SYSTEM_PERMISSIONS: {
    id: "system-permissions",
    title: "System Permissions",
    steps: ["meeting-intro", "meeting-permissions", "voice-permissions", "restart-if-required"],
    showTabs: false,
  },
  /**
   * Anything -after- system permissions should go here.
   * (Eg. anything related to using the "voice mode" feature, etc.)
   */
  // POST_SYSTEM_PERMISSIONS: {},
} as const;

export const Steps: StepMap = {
  ...NativeAppDownloadStepMap,
  ...IcpRfiStepsMap,
  ...ReferrerStepsMap,
  ...PostImportStepsMap,
  ...SystemPermissionsMeetingsStepsMap,
  ...SystemPermissionsVoiceStepsMap,
  "app-selection": {
    id: "app-selection",
    group: "import-cta",
    render: ({
      context,
      onUpdateContext,
      onNavToStep,
      selectedAppId,
      customAppName,
      onSetSelectedApp,
      onSetCustomAppName,
    }) => (
      <AppSelectionStep
        selectedAppId={selectedAppId || context.selectedAppId || null}
        customAppName={customAppName || context.customAppName || ""}
        onAppSelect={(appId: AppId) => {
          onSetSelectedApp?.(appId);
          onUpdateContext({ selectedAppId: appId });
        }}
        onCustomAppNameChange={(name: string) => {
          onSetCustomAppName?.(name);
          onUpdateContext({ customAppName: name });
        }}
        onNavToStep={onNavToStep}
      />
    ),
  },
  "skip-confirmation": {
    id: "skip-confirmation",
    group: "import-cta",
    render: props => <SkipConfirmationStep {...props} />,
  },
  ...AppleNotesStepsMap,
  ...ObsidianStepsMap,
  ...OtherAppsStepsMap,
} as const;
